// ============================================================================
// DATA VISUALIZER - مكتبة التصوير البصري للبيانات
// ============================================================================

export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    fill?: boolean;
    tension?: number;
  }[];
}

export interface ChartOptions {
  responsive?: boolean;
  maintainAspectRatio?: boolean;
  plugins?: {
    title?: {
      display?: boolean;
      text?: string;
      color?: string;
      font?: {
        size?: number;
        weight?: string;
      };
    };
    legend?: {
      display?: boolean;
      position?: 'top' | 'bottom' | 'left' | 'right';
      labels?: {
        color?: string;
        font?: {
          size?: number;
        };
      };
    };
  };
  scales?: {
    x?: {
      display?: boolean;
      title?: {
        display?: boolean;
        text?: string;
        color?: string;
      };
      ticks?: {
        color?: string;
      };
      grid?: {
        color?: string;
      };
    };
    y?: {
      display?: boolean;
      title?: {
        display?: boolean;
        text?: string;
        color?: string;
      };
      ticks?: {
        color?: string;
      };
      grid?: {
        color?: string;
      };
    };
  };
}

export interface VisualizationConfig {
  type: 'bar' | 'line' | 'pie' | 'doughnut' | 'scatter' | 'bubble' | 'radar' | 'polarArea';
  data: ChartData;
  options?: ChartOptions;
  height?: string;
  width?: string;
}

// ألوان متوافقة مع الهوية البصرية للتطبيق
export const chartColors = {
  primary: {
    light: '#8B5CF6', // purple-500
    dark: '#7C3AED', // purple-600
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
  },
  secondary: {
    light: '#EC4899', // pink-500
    dark: '#DB2777', // pink-600
    gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)'
  },
  success: {
    light: '#10B981', // green-500
    dark: '#059669', // green-600
    gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
  },
  warning: {
    light: '#F59E0B', // orange-500
    dark: '#D97706', // orange-600
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'
  },
  danger: {
    light: '#EF4444', // red-500
    dark: '#DC2626', // red-600
    gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
  },
  info: {
    light: '#3B82F6', // blue-500
    dark: '#2563EB', // blue-600
    gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
  },
  dark: {
    light: '#374151', // gray-700
    dark: '#1F2937', // gray-800
    gradient: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)'
  }
};

// مصفوفة الألوان للرسوم البيانية
export const chartColorPalette = [
  chartColors.primary.light,
  chartColors.secondary.light,
  chartColors.success.light,
  chartColors.warning.light,
  chartColors.danger.light,
  chartColors.info.light,
  chartColors.dark.light,
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#10B981', // green
  '#F59E0B', // orange
  '#EF4444', // red
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#F97316', // amber
  '#A855F7', // violet
  '#F43F5E', // rose
  '#14B8A6', // teal
];

// دالة لتحليل CSV
function parseCSV(csvText: string): any[] {
  const lines = csvText.trim().split('\n');
  if (lines.length === 0) return [];

  // استخراج العناوين
  const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
  
  // تحليل البيانات
  const data: any[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // تقسيم السطر مع مراعاة الفواصل داخل علامات التنصيص
    const values: string[] = [];
    let currentValue = '';
    let insideQuotes = false;
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      
      if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === ',' && !insideQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    
    // إضافة القيمة الأخيرة
    values.push(currentValue.trim());

    // إنشاء كائن من القيم
    const row: any = {};
    headers.forEach((header, index) => {
      let value = values[index] || '';
      
      // إزالة علامات التنصيص
      value = value.replace(/^"|"$/g, '');
      
      // محاولة تحويل القيمة إلى رقم إذا أمكن
      if (!isNaN(Number(value)) && value !== '') {
        row[header] = Number(value);
      } else if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
        row[header] = value.toLowerCase() === 'true';
      } else {
        row[header] = value;
      }
    });
    
    data.push(row);
  }
  
  return data;
}

// دالة لتحليل البيانات (JSON أو CSV)
function parseData(dataText: string): any[] {
  const trimmedText = dataText.trim();
  
  // محاولة تحليل كـ JSON أولاً
  try {
    return JSON.parse(trimmedText);
  } catch (jsonError) {
    // إذا فشل JSON، جرب CSV
    try {
      return parseCSV(trimmedText);
    } catch (csvError) {
      throw new Error('لا يمكن تحليل البيانات. تأكد من أن الملف بصيغة JSON أو CSV صحيحة.');
    }
  }
}

// دالة لتحليل البيانات وإنشاء رسوم بيانية
export function analyzeDataForVisualization(dataText: string): {
  charts: VisualizationConfig[];
  insights: string[];
  recommendations: string[];
} {
  let data: any[];
  
  try {
    data = parseData(dataText);
  } catch (error) {
    return {
      charts: [],
      insights: ['خطأ في تحليل البيانات: ' + (error as Error).message],
      recommendations: ['تأكد من أن البيانات بصيغة JSON أو CSV صحيحة']
    };
  }

  if (!data || data.length === 0) {
    return {
      charts: [],
      insights: ['لا توجد بيانات للتحليل'],
      recommendations: ['قم برفع ملف يحتوي على بيانات صالحة']
    };
  }

  const charts: VisualizationConfig[] = [];
  const insights: string[] = [];
  const recommendations: string[] = [];

  // تحليل الأعمدة
  const columns = Object.keys(data[0] || {});
  const numericColumns: string[] = [];
  const categoricalColumns: string[] = [];
  const dateColumns: string[] = [];

  columns.forEach(column => {
    const sampleValues = data.slice(0, 100).map(row => row[column]).filter(v => v !== null && v !== undefined);
    
    if (sampleValues.length === 0) return;

    const firstValue = sampleValues[0];
    
    // تحديد نوع العمود
    if (typeof firstValue === 'number' || !isNaN(Number(firstValue))) {
      numericColumns.push(column);
    } else if (firstValue instanceof Date || !isNaN(Date.parse(String(firstValue)))) {
      dateColumns.push(column);
    } else {
      categoricalColumns.push(column);
    }
  });

  insights.push(`تم العثور على ${columns.length} عمود في البيانات`);
  insights.push(`${numericColumns.length} عمود رقمي، ${categoricalColumns.length} عمود فئوي، ${dateColumns.length} عمود تاريخ`);

  // 1. رسم بياني دائري للفئات
  if (categoricalColumns.length > 0) {
    const categoricalColumn = categoricalColumns[0];
    const categoryCounts = data.reduce((acc: Record<string, number>, row: any) => {
      const value = String(row[categoricalColumn] || 'غير محدد');
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {});

    const topCategories = Object.entries(categoryCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8);

    if (topCategories.length > 0) {
      charts.push({
        type: 'doughnut',
        data: {
          labels: topCategories.map(([label]) => label),
          datasets: [{
            label: `توزيع ${categoricalColumn}`,
            data: topCategories.map(([, count]) => count),
            backgroundColor: chartColorPalette.slice(0, topCategories.length),
            borderWidth: 2,
            borderColor: '#ffffff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `توزيع ${categoricalColumn}`,
              color: '#374151',
              font: { size: 16, weight: 'bold' }
            },
            legend: {
              display: true,
              position: 'bottom',
              labels: { color: '#374151', font: { size: 12 } }
            }
          }
        },
        height: '300px'
      });

      insights.push(`أعلى فئة في ${categoricalColumn}: ${topCategories[0][0]} (${topCategories[0][1]} عنصر)`);
    }
  }

  // 2. رسم بياني شريطي للقيم الرقمية
  if (numericColumns.length > 0) {
    const numericColumn = numericColumns[0];
    const values = data.map(row => Number(row[numericColumn])).filter(v => !isNaN(v));
    
    if (values.length > 0) {
      const min = Math.min(...values);
      const max = Math.max(...values);
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      
      // تجميع البيانات في فئات
      const bins = 10;
      const binSize = (max - min) / bins;
      const histogram: number[] = new Array(bins).fill(0);
      
      values.forEach(value => {
        const binIndex = Math.min(Math.floor((value - min) / binSize), bins - 1);
        histogram[binIndex]++;
      });

      const binLabels = Array.from({ length: bins }, (_, i) => {
        const start = min + i * binSize;
        const end = min + (i + 1) * binSize;
        return `${start.toFixed(1)}-${end.toFixed(1)}`;
      });

      charts.push({
        type: 'bar',
        data: {
          labels: binLabels,
          datasets: [{
            label: `توزيع ${numericColumn}`,
            data: histogram,
            backgroundColor: chartColors.primary.gradient,
            borderColor: chartColors.primary.dark,
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `توزيع ${numericColumn}`,
              color: '#374151',
              font: { size: 16, weight: 'bold' }
            },
            legend: {
              display: false
            }
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: numericColumn,
                color: '#374151'
              },
              ticks: { color: '#374151' },
              grid: { color: '#E5E7EB' }
            },
            y: {
              display: true,
              title: {
                display: true,
                text: 'العدد',
                color: '#374151'
              },
              ticks: { color: '#374151' },
              grid: { color: '#E5E7EB' }
            }
          }
        },
        height: '300px'
      });

      insights.push(`${numericColumn}: المتوسط = ${avg.toFixed(2)}، الحد الأدنى = ${min.toFixed(2)}، الحد الأقصى = ${max.toFixed(2)}`);
    }
  }

  // 3. رسم بياني خطي للاتجاهات الزمنية
  if (dateColumns.length > 0 && numericColumns.length > 0) {
    const dateColumn = dateColumns[0];
    const numericColumn = numericColumns[0];
    
    const timeSeriesData = data
      .map(row => ({
        date: new Date(row[dateColumn]),
        value: Number(row[numericColumn])
      }))
      .filter(item => !isNaN(item.value) && !isNaN(item.date.getTime()))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (timeSeriesData.length > 0) {
      // تجميع البيانات حسب التاريخ
      const groupedData = timeSeriesData.reduce((acc: Record<string, number[]>, item) => {
        const dateKey = item.date.toISOString().split('T')[0];
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(item.value);
        return acc;
      }, {});

      const labels = Object.keys(groupedData).slice(-20); // آخر 20 نقطة
      const averages = labels.map(date => {
        const values = groupedData[date];
        return values.reduce((a, b) => a + b, 0) / values.length;
      });

      charts.push({
        type: 'line',
        data: {
          labels: labels.map(date => new Date(date).toLocaleDateString('ar-SA')),
          datasets: [{
            label: `متوسط ${numericColumn} عبر الزمن`,
            data: averages,
            borderColor: chartColors.success.light,
            backgroundColor: chartColors.success.light + '20',
            borderWidth: 3,
            fill: true,
            tension: 0.4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `اتجاه ${numericColumn} عبر الزمن`,
              color: '#374151',
              font: { size: 16, weight: 'bold' }
            },
            legend: {
              display: true,
              position: 'top',
              labels: { color: '#374151', font: { size: 12 } }
            }
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: 'التاريخ',
                color: '#374151'
              },
              ticks: { color: '#374151' },
              grid: { color: '#E5E7EB' }
            },
            y: {
              display: true,
              title: {
                display: true,
                text: numericColumn,
                color: '#374151'
              },
              ticks: { color: '#374151' },
              grid: { color: '#E5E7EB' }
            }
          }
        },
        height: '300px'
      });

      insights.push(`تم تحليل الاتجاه الزمني لـ ${numericColumn} عبر ${labels.length} نقطة زمنية`);
    }
  }

  // 4. رسم بياني مبعثر للعلاقات
  if (numericColumns.length >= 2) {
    const xColumn = numericColumns[0];
    const yColumn = numericColumns[1];
    
    const scatterData = data
      .map(row => ({
        x: Number(row[xColumn]),
        y: Number(row[yColumn])
      }))
      .filter(point => !isNaN(point.x) && !isNaN(point.y))
      .slice(0, 100); // حد أقصى 100 نقطة للوضوح

    if (scatterData.length > 0) {
      charts.push({
        type: 'scatter',
        data: {
          labels: [],
          datasets: [{
            label: `${xColumn} vs ${yColumn}`,
            data: scatterData,
            backgroundColor: chartColors.info.light + '80',
            borderColor: chartColors.info.dark,
            borderWidth: 1,
            pointRadius: 4,
            pointHoverRadius: 6
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `العلاقة بين ${xColumn} و ${yColumn}`,
              color: '#374151',
              font: { size: 16, weight: 'bold' }
            },
            legend: {
              display: true,
              position: 'top',
              labels: { color: '#374151', font: { size: 12 } }
            }
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: xColumn,
                color: '#374151'
              },
              ticks: { color: '#374151' },
              grid: { color: '#E5E7EB' }
            },
            y: {
              display: true,
              title: {
                display: true,
                text: yColumn,
                color: '#374151'
              },
              ticks: { color: '#374151' },
              grid: { color: '#E5E7EB' }
            }
          }
        },
        height: '300px'
      });

      // حساب معامل الارتباط
      const xValues = scatterData.map(p => p.x);
      const yValues = scatterData.map(p => p.y);
      const correlation = calculateCorrelation(xValues, yValues);
      
      insights.push(`معامل الارتباط بين ${xColumn} و ${yColumn}: ${correlation.toFixed(3)}`);
      
      if (Math.abs(correlation) > 0.7) {
        recommendations.push(`هناك علاقة قوية بين ${xColumn} و ${yColumn} (${correlation > 0 ? 'إيجابية' : 'سلبية'})`);
      }
    }
  }

  // توصيات عامة
  if (data.length > 1000) {
    recommendations.push('البيانات كبيرة الحجم - فكر في استخدام عينة للتحليل السريع');
  }
  
  if (numericColumns.length === 0) {
    recommendations.push('لا توجد أعمدة رقمية - أضف بيانات رقمية لتحليل إحصائي أفضل');
  }
  
  if (categoricalColumns.length === 0) {
    recommendations.push('لا توجد أعمدة فئوية - أضف بيانات فئوية لتحليل التوزيع');
  }

  return { charts, insights, recommendations };
}

// دالة حساب معامل الارتباط
function calculateCorrelation(x: number[], y: number[]): number {
  const n = x.length;
  if (n !== y.length || n === 0) return 0;

  const sumX = x.reduce((a, b) => a + b, 0);
  const sumY = y.reduce((a, b) => a + b, 0);
  const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
  const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0);
  const sumY2 = y.reduce((sum, yi) => sum + yi * yi, 0);

  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

  return denominator === 0 ? 0 : numerator / denominator;
}

// دالة لإنشاء إحصائيات سريعة
export function createDataSummary(dataText: string): {
  totalRecords: number;
  totalColumns: number;
  numericColumns: number;
  categoricalColumns: number;
  dateColumns: number;
  missingData: number;
  completeness: number;
} {
  let data: any[];
  
  try {
    data = parseData(dataText);
  } catch (error) {
    return {
      totalRecords: 0,
      totalColumns: 0,
      numericColumns: 0,
      categoricalColumns: 0,
      dateColumns: 0,
      missingData: 0,
      completeness: 0
    };
  }

  if (!data || data.length === 0) {
    return {
      totalRecords: 0,
      totalColumns: 0,
      numericColumns: 0,
      categoricalColumns: 0,
      dateColumns: 0,
      missingData: 0,
      completeness: 0
    };
  }

  const columns = Object.keys(data[0] || {});
  const totalRecords = data.length;
  const totalColumns = columns.length;
  
  let numericColumns = 0;
  let categoricalColumns = 0;
  let dateColumns = 0;
  let missingData = 0;

  columns.forEach(column => {
    const sampleValues = data.slice(0, 100).map(row => row[column]).filter(v => v !== null && v !== undefined);
    
    if (sampleValues.length === 0) {
      missingData += totalRecords;
      return;
    }

    const firstValue = sampleValues[0];
    
    if (typeof firstValue === 'number' || !isNaN(Number(firstValue))) {
      numericColumns++;
    } else if (firstValue instanceof Date || !isNaN(Date.parse(String(firstValue)))) {
      dateColumns++;
    } else {
      categoricalColumns++;
    }

    // حساب البيانات المفقودة
    missingData += data.filter(row => row[column] === null || row[column] === undefined).length;
  });

  const totalCells = totalRecords * totalColumns;
  const completeness = totalCells > 0 ? ((totalCells - missingData) / totalCells) * 100 : 0;

  return {
    totalRecords,
    totalColumns,
    numericColumns,
    categoricalColumns,
    dateColumns,
    missingData,
    completeness: Math.round(completeness * 100) / 100
  };
}

// دالة لإنشاء ملخص بصري للبيانات
export function createDataSummaryVisual(dataText: string): {
  summary: string;
  keyMetrics: Array<{ label: string; value: string; color: string }>;
} {
  const stats = createDataSummary(dataText);
  
  const summary = `تحليل ${stats.totalRecords.toLocaleString()} سجل في ${stats.totalColumns} عمود`;
  
  const keyMetrics = [
    {
      label: 'إجمالي السجلات',
      value: stats.totalRecords.toLocaleString(),
      color: chartColors.primary.light
    },
    {
      label: 'الأعمدة الرقمية',
      value: stats.numericColumns.toString(),
      color: chartColors.success.light
    },
    {
      label: 'الأعمدة الفئوية',
      value: stats.categoricalColumns.toString(),
      color: chartColors.warning.light
    },
    {
      label: 'اكتمال البيانات',
      value: `${stats.completeness}%`,
      color: stats.completeness > 90 ? chartColors.success.light : 
             stats.completeness > 70 ? chartColors.warning.light : chartColors.danger.light
    }
  ];

  return { summary, keyMetrics };
} 